<script setup>
import { computed } from 'vue'

/**
 * Consts representing the ETA statuses.
 */
const WARM = 'warn'
const LATE = 'late'

/**
 * The component's inherited properties.
 */
const props = defineProps({
    /**
     * The given load.
     *
     * @type {Object}
     */
    load: {
        type: Object,
        required: true,
    },
})

/**
 * The load's ETA status.
 *
 * @return {Array}
 */
const etaStatus = computed(() => {
    return props.load?.eta?.status
})
</script>

<template>
    <div>
        <v-icon
            v-if="etaStatus === LATE"
            data-test="eta-icon"
            :id="`eta-icon_row-${load.id}`"
            icon="fas fa-circle"
            color="#dc2626"
            size="small"
            class="me-2"
        />
        <v-icon
            v-else-if="etaStatus === WARM"
            data-test="eta-icon"
            :id="`eta-icon_row-${load.id}`"
            icon="fas fa-circle"
            color="#fbbf24"
            size="small"
            class="me-2"
        >
        </v-icon>
        <v-icon
            v-else
            data-test="eta-icon"
            :id="`eta-icon_row-${load.id}`"
            icon="fas fa-circle"
            color="#119977"
            size="small"
            class="me-2"
        >
        </v-icon>
        <v-tooltip
            v-if="etaStatus === LATE"
            data-test="eta-tooltip"
            :activator="`#eta-icon_row-${load.id}`"
            location="bottom"
        >
            Current stop is running late
        </v-tooltip>
        <v-tooltip
            v-else-if="etaStatus === WARM"
            data-test="eta-tooltip"
            :activator="`#eta-icon_row-${load.id}`"
            location="bottom"
        >
            Current stop might be late
        </v-tooltip>
        <v-tooltip
            v-else
            data-test="eta-tooltip"
            :activator="`#eta-icon_row-${load.id}`"
            location="bottom"
        >
            Current stop is on time
        </v-tooltip>
        <v-icon
            v-if="load?.summary?.has_unassigned_segment"
            data-test="unassigned-segment-icon"
            icon="far fa-clipboard-question"
            size="small"
            class="me-2"
            title="Has unassigned segment"
        >
        </v-icon>
        <v-icon
            v-if="load?.summary?.has_temperature_controlled_shipment"
            data-test="temperature-controlled-icon"
            icon="far fa-temperature-list"
            size="small"
            class="me-2"
            title="Has temperature controlled shipment"
        >
        </v-icon>
        <v-icon
            v-if="load?.summary?.has_backhaul_shipment"
            data-test="backhaul-icon"
            icon="far fa-arrow-right-arrow-left"
            size="small"
            class="me-2"
            title="Has backhaul shipment"
        >
        </v-icon>
        <v-icon
            v-if="load?.summary?.has_hazmat_shipment"
            data-test="hazmat-icon"
            icon="far fa-diamond-exclamation"
            size="small"
            class="me-2"
            title="Has hazmat shipment"
        >
        </v-icon>
        <v-icon
            v-if="load.comment_count > 0"
            data-test="comments-icon"
            icon="far fa-comment"
            size="small"
            class="me-2"
            title="Has load comments"
        >
        </v-icon>
        <v-icon
            v-if="load?.summary?.has_edi_shipment === true"
            data-test="edi-icon"
            icon="far fa-file-binary"
            size="small"
            class="me-2"
            title="EDI Load"
        >
        </v-icon>
    </div>
</template>
